import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { allTools } from '../../mocks/tools';

// ─────────────────────────────────────────────────────────────────────────────
// FOUNDER CONFIG
// Fill in the fields below with real details — they appear in the
// "Who Builds ToolNova" section of the About page.
// ─────────────────────────────────────────────────────────────────────────────
const FOUNDER = {
  name: 'Reece Satran',
  title: 'Founder',
  photo: 'https://storage.readdy-site.link/project_files/4a87f1ed-ca71-4188-a2d0-a2c423e3889a/0843357d-b0b2-4257-8c94-8b81daf15e1b_IMG_2270.png?v=0dead78c437552edd7e15c3e49587cbc',
  bio: [
    `I\'m Reece, the founder of ToolNova.ai. After realizing how hard it was to compare AI tools across scattered blogs and sales pages, I decided to build one clear directory instead. ToolNova exists to help you—whether you\'re shipping your own tool or just getting started with AI—find reliable options without wasting hours researching.`,
  ],
  twitter: 'https://x.com/toolnovaHQ',
  instagram: 'https://instagram.com/toolnova.ai',
  tiktok: 'https://tiktok.com/@toolnova.ai',
  youtube: 'https://youtube.com/@toolnovaHQ',
  linkedIn: '',
};
// ─────────────────────────────────────────────────────────────────────────────

const REVIEW_STEPS = [
  {
    icon: 'ri-eye-line',
    title: 'Discovery',
    desc: 'We monitor ProductHunt, X, FutureTools, and AI newsletters daily to catch new tools early.',
  },
  {
    icon: 'ri-test-tube-line',
    title: 'Hands-on Testing',
    desc: 'Each tool is evaluated across its primary use cases before being listed on the directory.',
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Pricing Audit',
    desc: 'We verify pricing tiers and clearly label whether a tool is Free, Freemium, or Paid.',
  },
  {
    icon: 'ri-check-double-line',
    title: 'Editorial Review',
    desc: 'We write clear, honest summaries including genuine pros, cons, and who the tool is best for.',
  },
];

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';
  const totalTools = allTools.length;

  useSEO({
    title: 'About Reece Satran — Founder of ToolNova.ai',
    description: 'Meet Reece Satran, the founder of ToolNova.ai — an AI tools discovery platform built to help everyday people find the right AI tools without the overwhelm.',
    keywords: 'about toolnova, Reece Satran, AI tools directory, who built toolnova, AI software discovery',
    canonical: `${siteUrl}/about`,
    ogTitle: 'About Reece Satran — Founder of ToolNova.ai',
    ogDescription: 'Meet Reece Satran, the founder of ToolNova.ai — an AI tools discovery platform built to help everyday people find the right AI tools without the overwhelm.',
    ogType: 'website',
    ogUrl: `${siteUrl}/about`,
    ogImage: 'https://public.readdy.ai/ai/img_res/2a35743cdb9600edc45f9b5e09018b91.jpg',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'About ToolNova',
        description: 'ToolNova is a free AI tools directory helping creators and businesses discover the best AI software for work, creativity, and growth.',
        url: `${siteUrl}/about`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'ToolNova',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/81ddcca4e455aa4e3e9404d7fe6eefde/e7fd262c64409f252189cfe0fbf7a092.png',
        },
        description: 'ToolNova helps creators, entrepreneurs, and businesses discover the most useful AI software to boost productivity, creativity, and growth.',
        email: 'toolnova.hq@gmail.com',
        sameAs: [
          'https://x.com/toolnovaHQ',
          'https://instagram.com/toolnova.ai',
          'https://tiktok.com/@toolnova.ai',
          'https://youtube.com/@toolnovaHQ',
        ],
        foundingDate: '2025',
        knowsAbout: ['Artificial Intelligence', 'AI Tools', 'Machine Learning Software', 'Productivity Software'],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(45,212,191,0.08),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(6,182,212,0.06),transparent_50%)]"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-8">
            <i className="ri-information-line text-teal-400"></i>
            <span className="text-sm font-medium text-teal-300">About Toolnova</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Your Trusted AI Tools
            <span className="block bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Discovery Platform
            </span>
          </h1>
          
          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Toolnova helps creators, entrepreneurs, and businesses discover the most useful AI software to boost productivity, creativity, and growth.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Our Mission
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                In a world where new AI tools launch every day, finding the right software for your needs can be overwhelming. Toolnova was created to solve this problem by curating and reviewing the best AI tools across every category.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We believe AI technology should be accessible to everyone, not just tech experts. Our platform makes it easy to discover, compare, and choose the perfect AI tools for your specific use case.
              </p>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-2xl blur-3xl"></div>
              <div className="relative bg-gray-800/60 backdrop-blur-sm border border-gray-700/60 rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { number: `${totalTools}+`, label: 'AI Tools Listed' },
                    { number: '9', label: 'Categories' },
                    { number: 'Weekly', label: 'New Tools Added' },
                    { number: '100%', label: 'Free Access' },
                  ].map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-3xl font-bold text-teal-400 mb-2">{stat.number}</div>
                      <div className="text-sm text-gray-400">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Builds ToolNova */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Who Builds ToolNova
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Built by someone who needed it — a research-driven directory for anyone navigating the AI tools landscape.
            </p>
          </div>

          <div className="bg-gray-800/50 border border-gray-700/60 rounded-2xl p-8 md:p-10">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10 items-start">
              {/* Photo — centered on mobile, left on desktop */}
              <div className="flex-shrink-0 flex flex-col items-center md:items-start w-full md:w-auto">
                <div className="w-32 h-32 md:w-36 md:h-36 rounded-2xl overflow-hidden border-2 border-teal-500/50 ring-4 ring-teal-500/10">
                  <img
                    src={FOUNDER.photo}
                    alt={FOUNDER.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Bio */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h3 className="text-2xl font-bold text-white">{FOUNDER.name}</h3>
                </div>
                <p className="text-teal-400 text-sm font-semibold tracking-wide mb-1">{FOUNDER.title}</p>
                <p className="text-gray-500 text-xs mb-5">Founded in 2026</p>

                <div className="space-y-4 mb-6">
                  {FOUNDER.bio.map((paragraph, idx) => (
                    <p key={idx} className="text-gray-300 leading-relaxed text-sm">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Social buttons */}
                <div className="flex items-center flex-wrap gap-3">
                  <a
                    href={FOUNDER.twitter}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700/60 hover:bg-gray-700 border border-gray-600/60 hover:border-teal-500/50 rounded-lg text-gray-200 hover:text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-twitter-x-line text-sm"></i>
                    </div>
                    @toolnovaHQ
                  </a>
                  <a
                    href={FOUNDER.instagram}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700/60 hover:bg-gray-700 border border-gray-600/60 hover:border-teal-500/50 rounded-lg text-gray-200 hover:text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-instagram-line text-sm"></i>
                    </div>
                    @toolnova.ai
                  </a>
                  <a
                    href={FOUNDER.tiktok}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700/60 hover:bg-gray-700 border border-gray-600/60 hover:border-teal-500/50 rounded-lg text-gray-200 hover:text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-tiktok-line text-sm"></i>
                    </div>
                    @toolnova.ai
                  </a>
                  <a
                    href={FOUNDER.youtube}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700/60 hover:bg-gray-700 border border-gray-600/60 hover:border-teal-500/50 rounded-lg text-gray-200 hover:text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-youtube-line text-sm"></i>
                    </div>
                    @toolnovaHQ
                  </a>
                </div>
              </div>
            </div>

            {/* What ToolNova Is */}
            <div className="mt-8 pt-8 border-t border-gray-700/50">
              <h4 className="text-base font-bold text-white mb-2">What ToolNova Is</h4>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                ToolNova is a free AI tools directory built for curious people, not engineers. Browse 150+ tools by category, take the quiz to find your perfect match, or submit your own tool to be featured.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: 'ri-tools-line', label: '150+ Tools Listed' },
                  { icon: 'ri-lock-unlock-line', label: 'Free to Browse' },
                  { icon: 'ri-calendar-line', label: 'New Tools Added Weekly' },
                ].map((badge) => (
                  <div
                    key={badge.label}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/25 rounded-full"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${badge.icon} text-teal-400 text-xs`}></i>
                    </div>
                    <span className="text-teal-300 text-xs font-medium">{badge.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Review Tools */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How We Review Tools
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Every tool listed on ToolNova goes through a consistent research and evaluation process before being published.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {REVIEW_STEPS.map((step, index) => (
              <div
                key={index}
                className="group relative p-6 bg-gray-900/70 border border-gray-800/80 rounded-xl hover:border-teal-500/50 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-500/15 rounded-lg group-hover:bg-teal-500/25 transition-colors">
                    <i className={`${step.icon} text-xl text-teal-400`}></i>
                  </div>
                  <span className="text-xs font-bold text-teal-500/70 uppercase tracking-widest">Step {index + 1}</span>
                </div>
                <h3 className="text-base font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/40">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Toolnova Offers
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Everything you need to find and evaluate the best AI tools for your workflow
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-folder-line',
                title: 'Curated Directory',
                description: 'Browse hundreds of carefully selected AI tools organized by category, use case, and industry.',
              },
              {
                icon: 'ri-star-line',
                title: 'Unbiased Reviews',
                description: 'Get honest, detailed reviews and comparisons to help you make informed decisions about AI software.',
              },
              {
                icon: 'ri-notification-line',
                title: 'Latest AI News',
                description: 'Stay updated with the newest AI tools, features, and trends through our regularly updated blog.',
              },
              {
                icon: 'ri-search-line',
                title: 'Smart Search',
                description: 'Find exactly what you need with powerful search and filtering options across all categories.',
              },
              {
                icon: 'ri-price-tag-3-line',
                title: 'Pricing Transparency',
                description: 'Compare pricing plans and features to find AI tools that fit your budget and requirements.',
              },
              {
                icon: 'ri-community-line',
                title: 'Community Driven',
                description: 'Submit your favorite AI tools and help others discover great software through our community.',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="group bg-gray-800/40 backdrop-blur-sm border border-gray-700/60 rounded-xl p-6 hover:border-teal-500/60 hover:bg-gray-800/60 transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/10 cursor-default"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500/10 border border-teal-500/30 rounded-lg mb-4 group-hover:bg-teal-500/20 transition-colors duration-300">
                  <i className={`${feature.icon} text-2xl text-teal-400`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              The principles that guide everything we do at Toolnova
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Transparency',
                description: 'We clearly disclose affiliate relationships and always provide honest, unbiased reviews of AI tools.',
              },
              {
                icon: 'ri-user-heart-line',
                title: 'User First',
                description: 'Every decision we make prioritizes helping our users find the best AI tools for their specific needs.',
              },
              {
                icon: 'ri-lightbulb-line',
                title: 'Innovation',
                description: 'We stay ahead of AI trends to bring you the latest and most innovative tools as soon as they launch.',
              },
              {
                icon: 'ri-team-line',
                title: 'Community',
                description: 'We build a collaborative space where users can share discoveries and help each other succeed with AI.',
              },
            ].map((value, index) => (
              <div
                key={index}
                className="flex gap-6 bg-gray-800/40 backdrop-blur-sm border border-gray-700/60 rounded-xl p-6 hover:border-teal-500/40 transition-all duration-300"
              >
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/30 rounded-xl">
                    <i className={`${value.icon} text-2xl text-teal-400`}></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border border-teal-500/30 rounded-2xl p-12 text-center">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(45,212,191,0.1),transparent_70%)]"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Discover AI Tools?
              </h2>
              <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                Explore our curated directory of the best AI tools for work, creativity, and business growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-teal-400 hover:to-cyan-400 transition-all duration-300 shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <span>Explore AI Tools</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/submit-tool"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border border-teal-500/50 hover:border-teal-400 text-teal-400 hover:text-teal-300 font-semibold rounded-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-circle-line"></i>
                  <span>Submit Your Tool</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
